import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Plus, Minus, X, ArrowRight, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

const formatPrice = (paiseStr, currency = 'INR') => {
  const rupees = parseInt(paiseStr || '0', 10) / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(rupees);
};

const FREE_SHIPPING_AT_PAISE = 40000; // ₹400

// Build Your Own Box items carry their flavor selection back from the
// Store API under extensions.raretto_build_box.flavors (see the
// raretto-headless.php plugin's Build-a-Box section).
const buildBoxFlavors = (item) => item.extensions?.raretto_build_box?.flavors;

const Cart = () => {
  const {
    cartItems,
    updateItem,
    removeItem,
    cart,
    cartLoading,
    applyCoupon,
  } = useCart();

  const [coupon, setCoupon] = useState('');

  const totals = cart?.totals ?? {};
  const itemCount = cartItems.length;
  const subtotalPaise = parseInt(totals.total_items || '0', 10);
  const discountPaise = parseInt(totals.total_discount || '0', 10);
  const shippingPaise = parseInt(totals.total_shipping || '0', 10);

  const onApplyCoupon = async (e) => {
    e.preventDefault();
    if (!coupon.trim()) return;
    try {
      await applyCoupon(coupon.trim());
      setCoupon('');
      toast.success('Coupon applied');
    } catch {
      /* queryClient mutation onError already toasts */
    }
  };

  if (cartLoading) {
    return (
      <main className="container-lux py-32 text-center">
        <p className="text-[#1a120b]/60">Loading your bag…</p>
      </main>
    );
  }

  if (itemCount === 0) {
    return (
      <main className="container-lux py-32 text-center">
        <ShoppingBag className="w-12 h-12 text-[#1a120b]/30 mx-auto mb-6" />
        <p className="font-display text-5xl mb-4">Your box is empty</p>
        <p className="text-[#1a120b]/60 mb-8 max-w-md mx-auto">
          Curate a signature box, or start with a bestseller.
        </p>
        <div className="flex gap-3 justify-center">
          <Link to="/shop" className="btn-ghost">
            Shop
          </Link>
          <Link to="/build-a-box" className="btn-primary">
            Build a Box
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="container-lux py-14">
      <div className="mb-10">
        <p className="tracking-luxe text-[#b78d3c] mb-3">Review</p>
        <h1 className="font-display text-5xl md:text-6xl">Your Bag</h1>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-10">
        <div>
          <ul className="divide-y divide-[#1a120b]/12">
            {cartItems.map((item) => {
              const img =
                item.images?.[0]?.thumbnail || item.images?.[0]?.src;
              const currency = item.prices?.currency_code || 'INR';
              const flavors = buildBoxFlavors(item);
              return (
                <li key={item.key} className="py-6 flex gap-5">
                  <div className="w-28 h-28 rounded-full overflow-hidden bg-[#efe6d3] flex items-center justify-center flex-shrink-0">
                    {img ? (
                      <img
                        src={img}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="font-display text-3xl text-[#b78d3c]">
                        R
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <div>
                        <p className="font-display text-2xl leading-tight">
                          {item.name}
                        </p>
                        {flavors?.length > 0 ? (
                          <p className="text-xs text-[#1a120b]/60 mt-1">
                            {flavors.map((f) => `${f.count}× ${f.name}`).join(', ')}
                          </p>
                        ) : (
                          <Link
                            to={`/product/${item.id}`}
                            className="text-xs text-[#1a120b]/60 hover:text-[#1a120b] mt-1 inline-block"
                          >
                            View product →
                          </Link>
                        )}
                      </div>
                      <button
                        onClick={() => removeItem(item.key)}
                        className="text-[#1a120b]/50 hover:text-[#1a120b]"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      {flavors?.length > 0 ? (
                        // Quantity locked to 1 for a custom box — see
                        // CartDrawer.jsx for why (flavor breakdown
                        // describes one box; add another via the builder).
                        <span className="text-sm text-[#1a120b]/50">
                          Qty 1 · custom box
                        </span>
                      ) : (
                      <div className="flex items-center border border-[#1a120b]">
                        <button
                          onClick={() =>
                            updateItem({
                              key: item.key,
                              quantity: Math.max(1, item.quantity - 1),
                            })
                          }
                          className="w-9 h-9 flex items-center justify-center"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-10 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateItem({
                              key: item.key,
                              quantity: item.quantity + 1,
                            })
                          }
                          className="w-9 h-9 flex items-center justify-center"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      )}
                      <p className="font-display text-xl">
                        {formatPrice(
                          item.totals?.line_total || item.prices?.price,
                          currency
                        )}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <aside className="lg:sticky lg:top-28 h-fit">
          <div className="bg-[#1a120b] text-[#f6efe1] p-7">
            <p className="tracking-luxe text-[#b78d3c] mb-2">Order</p>
            <h3 className="font-display text-3xl mb-6">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#f6efe1]/70">Subtotal</span>
                <span>{formatPrice(subtotalPaise)}</span>
              </div>
              {discountPaise > 0 && (
                <div className="flex justify-between text-[#b78d3c]">
                  <span>Discount</span>
                  <span>− {formatPrice(discountPaise)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-[#f6efe1]/70">Shipping</span>
                <span>
                  {shippingPaise === 0
                    ? 'Complimentary'
                    : formatPrice(shippingPaise)}
                </span>
              </div>
              {shippingPaise > 0 && (
                <p className="text-xs text-[#f6efe1]/50">
                  Add{' '}
                  {formatPrice(FREE_SHIPPING_AT_PAISE - subtotalPaise)} more
                  for free shipping
                </p>
              )}
              <div className="gold-rule my-3"></div>
              <div className="flex justify-between font-display text-2xl">
                <span>Total</span>
                <span>{formatPrice(totals.total_price || '0')}</span>
              </div>
            </div>

            <form
              onSubmit={onApplyCoupon}
              className="mt-6 flex gap-2"
              aria-label="Apply coupon"
            >
              <input
                type="text"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="Coupon code"
                className="flex-1 bg-[#1a120b] border border-[#f6efe1]/30 text-[#f6efe1] placeholder:text-[#f6efe1]/40 px-3 py-2 text-sm"
              />
              <button
                type="submit"
                className="text-xs tracking-widest uppercase px-4 py-2 border border-[#b78d3c] text-[#b78d3c] hover:bg-[#b78d3c] hover:text-[#1a120b]"
              >
                Apply
              </button>
            </form>

            {cart?.coupons && cart.coupons.length > 0 && (
              <div className="mt-3 text-xs text-[#b78d3c]">
                Coupon: {cart.coupons.map((c) => c.code).join(', ')}
              </div>
            )}

            <Link to="/checkout" className="btn-gold w-full mt-6">
              Checkout <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default Cart;
